import React from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { useMutation } from '@apollo/react-hooks';
import { Typography, Button } from '@material-ui/core';
import { useSnackbar } from 'notistack';

import { SimpleInput, LineOnSidesLink } from 'components';
import {
  isRequired,
  isValidPassword,
  isConfirmPassword
} from 'utils/validators';

import { useField, useForm } from 'custom-hooks';

import { UPDATE_BUSINESS_OWNER } from 'graphql/mutations/business-owner';
import { CHECK_TOKEN } from 'graphql/mutations/token';

import useStyles from './styles';

const CreateNewPassword = ({ isReset, isConfirm }) => {
  const classes = useStyles();

  const { id, token } = useParams();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [tokenExpired, setTokenExpired] = React.useState(null);

  const [updatePassword] = useMutation(UPDATE_BUSINESS_OWNER);
  const [checkToken] = useMutation(CHECK_TOKEN);

  React.useEffect(() => {
    checkToken({
      variables: {
        id: id,
        token: token,
        isResetPassword: isReset,
        isConfirmPassword: isConfirm
      }
    }).then(
      () => setTokenExpired(false),
      () => setTokenExpired(true)
    );
  }, []);

  const createNewPasswordForm = useForm({
    onSubmit: (formData, formValid) => {
      if (!formValid) return;

      updatePassword({
        variables: {
          id: id,
          password: formData.password,
          token: token,
          isResetPassword: isReset,
          isConfirmPassword: isConfirm
        }
      }).then(
        () => {
          const msg = isConfirm
            ? 'Your password has been successfully created'
            : 'Your password has been successfully reset';
          enqueueSnackbar(msg, {
            variant: 'success'
          });
          history.push('/login');
        },
        () => setTokenExpired(true)
      );
    }
  });

  const passwordField = useField('password', createNewPasswordForm, {
    defaultValue: '',
    validations: [
      formData => isRequired(formData.password, 'Password is required'),
      formData =>
        isValidPassword(
          formData.password,
          'The password must include at least 8 characters with 1 symbol, 1 digit, 1 uppercase letter, 1 lowercase letter'
        )
    ]
  });

  const confirmPasswordField = useField(
    'confirmPassword',
    createNewPasswordForm,
    {
      defaultValue: '',
      validations: [
        formData =>
          isRequired(formData.confirmPassword, 'Confirm password is required'),
        formData =>
          isConfirmPassword(
            formData.confirmPassword,
            passwordField.value,
            'Your password and confirmation password do not match'
          )
      ]
    }
  );

  if (tokenExpired === null) {
    return false;
  }

  if (tokenExpired) {
    return (
      <div className={classes.tokeExpiredWrapper}>
        <div>
          <Typography variant="h1">
            The link you followed has expired
          </Typography>
          <div className={classes.backLogin}>
            <LineOnSidesLink path="/login"> Back to Login</LineOnSidesLink>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.wrapper}>
      <Typography className={classes.title} variant="h1">
        Create new Password
      </Typography>

      <form onSubmit={createNewPasswordForm.handleSubmit}>
        <SimpleInput
          type="password"
          placeholder="Password"
          label="Password"
          value={passwordField.value}
          onChange={passwordField.onChange}
          errors={passwordField.errors}
          errorText={passwordField.errors[0]}
          autoComplete="new-password"
        />
        <SimpleInput
          className={classes.inputIndent}
          type="password"
          placeholder="Confirm password"
          label="Confirm password"
          value={confirmPasswordField.value}
          onChange={confirmPasswordField.onChange}
          errors={confirmPasswordField.errors}
          errorText={confirmPasswordField.errors[0]}
          autoComplete="new-password"
        />
        <Button
          type="submit"
          className={classes.button}
          variant="contained"
          color="primary"
        >
          Create
        </Button>
      </form>
    </div>
  );
};

export default CreateNewPassword;
